import { FunctionComponent, useState, useEffect } from "react";
import CTAFold, { CTAFoldContent, CTAFoldCTA } from "manifest-components/src/Containers/CTAFold";
import { Container, FlexContainer, Spacing } from "manifest-components/src/Containers";
import { RadioTile } from "manifest-components/src/Tiles/RadioTile";
import { Button } from "manifest-components/src/Button";
import { ColorClassNames } from "manifest-components/src/Colors";
import PhoneNumber from "manifest-components/src/PhoneNumber";
import * as Styled from "./styles";
import ConfirmEmployerController, { EmploymentStatus, EmployerOptionsProps } from "./ConfirmEmployerController";

export const PEHPAccountCreationContent = () => {
  return (
    <>
      <Spacing $space={40} />
      <h4>Open your Utah Retirement Systems (URS) account</h4>
      <Spacing $space={4} />
      <p>To check your eligibility and open your account, contact URS. </p>
      <Spacing $space={26} />
      <p>
        <Styled.Organization>Phone: </Styled.Organization>
        <PhoneNumber phoneNumber="8013667700" />
      </p>
      <Spacing $space={26} />
      <p className="bold">
        Once you open your URS 401(k) or 457(b), return to Manifest to continue with your transfer.
      </p>
    </>
  );
};

export type HasURSAccount = "401k" | "457b" | "not-yet" | undefined;

interface PEHPPreAccountCreationScreenProps extends EmployerOptionsProps {
  goBackHome: () => void;
}

const PEHPPreAccountCreationScreen: FunctionComponent<PEHPPreAccountCreationScreenProps> = (
  props: PEHPPreAccountCreationScreenProps
) => {
  const { goBackHome } = props;
  const [isMember, setIsMemeber] = useState<boolean | undefined>(undefined);
  const [isEmployee, setIsEmployee] = useState<boolean | undefined>(undefined);
  const [hasURSAccount, setHasURSAccount] = useState<HasURSAccount>(undefined);
  const [showOpenAccountScreen, setShowOpenAccountScreen] = useState<boolean>(false);

  const controller = ConfirmEmployerController(props);
  const { employmentStatus, handleContinue, setEmploymentStatus } = controller;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  if (showOpenAccountScreen) {
    return (
      <>
        <PEHPAccountCreationContent />
        <Spacing $space={40} />
        <Button name="returnHome" text="Return home" onClick={goBackHome} />
      </>
    );
  }

  return (
    <CTAFold>
      <CTAFoldContent>
        <Container className="header-section-a">
          <h3 className={ColorClassNames.coco}>Let's get started!</h3>
          <Spacing $mobileSpace={4} $laptopSpace={4} />
          <p className="small">Share your current status to help us determine the best way to proceed.</p>
        </Container>

        <Container className="tile-section-a">
          <p className={`${ColorClassNames.coco} large bold`}>Are you a PEHP member?</p>
          <FlexContainer $flexDirection="column" $alignItems="center" className="tile-section-a">
            <RadioTile
              title={"Yes, I'm currently covered under PEHP or COBRA"}
              selected={isMember}
              onClick={() => {
                setIsMemeber(true);
              }}
            />
            <RadioTile
              title="No, I’m no longer covered under PEHP or COBRA"
              selected={isMember === false}
              onClick={() => {
                setIsMemeber(false);
                setEmploymentStatus(EmploymentStatus.neverWorked);
              }}
            />
          </FlexContainer>
          <Spacing $mobileSpace={40} $laptopSpace={40} />
          <p>
            <p className={`${ColorClassNames.coco} large bold`}>Are you working for the state of Utah?</p>
            <p className="small regular">
              State employees serve in various state government agencies, e.g., Utah Department of Natural Resources and
              the Utah State Board of Education. For a complete list, visit{" "}
              <Styled.Link className="bold" href="https://www.utah.gov/government/agencylist.html" target="_blank">
                here
              </Styled.Link>
              .
            </p>
          </p>

          <FlexContainer $flexDirection="column" $alignItems="center" className="tile-section-a">
            <RadioTile
              title="Yes, I’m currently a Utah state employee"
              selected={isEmployee}
              onClick={() => {
                setIsEmployee(true);
                if (isMember) {
                  setEmploymentStatus(EmploymentStatus.currentlyWorks);
                } else if (isMember === false) {
                  setEmploymentStatus(EmploymentStatus.neverWorked);
                }
              }}
            />
            <RadioTile
              title="No, I’m no longer a Utah state employee"
              selected={isEmployee === false}
              onClick={() => {
                setIsEmployee(false);
                if (isMember) {
                  setEmploymentStatus(EmploymentStatus.previouslyWorked);
                } else if (isMember === false) {
                  setEmploymentStatus(EmploymentStatus.neverWorked);
                }
              }}
            />
          </FlexContainer>
          {isEmployee && isMember && (
            <>
              <Spacing $mobileSpace={40} $laptopSpace={40} />
              <p>
                <p className={`${ColorClassNames.coco} large bold`}>
                  Do you have a Utah Retirement Systems 401(k) or 457(b)?
                </p>
                <p className="small regular">
                  Some URS system employers use automatic enrollment, so you might already have an URS account.
                  <br />
                  To check your account and plan type, log into your myURS account and view your quarterly statement at{" "}
                  <Styled.Link className="bold" href="https://www.urs.org/MyUrs/Account/Logon" target="_blank">
                    myURS
                  </Styled.Link>
                  .
                </p>
              </p>
              <FlexContainer $flexDirection="column" $alignItems="center" className="tile-section-a">
                <RadioTile
                  title="I have a 401(k)"
                  selected={hasURSAccount === "401k"}
                  onClick={() => setHasURSAccount("401k")}
                />
                <RadioTile
                  title="I have a 457(b)"
                  selected={hasURSAccount === "457b"}
                  onClick={() => setHasURSAccount("457b")}
                />
                <RadioTile
                  title="I need to open my 401(k) or 457(b)"
                  selected={hasURSAccount === "not-yet"}
                  onClick={() => setHasURSAccount("not-yet")}
                />
              </FlexContainer>
            </>
          )}
        </Container>
      </CTAFoldContent>
      <CTAFoldCTA>
        <div className="center-text">
          <Button
            name="continue"
            text="Continue"
            onClick={
              hasURSAccount !== undefined && hasURSAccount === "not-yet"
                ? () => setShowOpenAccountScreen(true)
                : handleContinue
            }
            disabled={employmentStatus === null || (isEmployee && isMember && hasURSAccount === undefined)}
          />
        </div>
      </CTAFoldCTA>
    </CTAFold>
  );
};

export default PEHPPreAccountCreationScreen;
